import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Ticket, ShieldCheck, Timer, AlertCircle, Loader2 } from 'lucide-react';

const NAVY = '#07122f';
const TEAL = '#007f73';

/**
 * SurgicalGate — full-page paywall shown when the doctor has NO active
 * Surgical Suite entitlement. Left column shows the offline UPI payment flow
 * (QR + list price + copy-friendly VPA + step-by-step WhatsApp instructions);
 * right column takes the admin-issued coupon code to activate access.
 */
export default function SurgicalGate({ status, onActivated }) {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const vpa = status.upi_vpa;
  const payee = status.upi_payee_name;
  const price = status.list_price_inr;
  const upiUri = `upi://pay?pa=${encodeURIComponent(vpa)}&pn=${encodeURIComponent(payee)}&am=${price}&cu=INR&tn=SurgicalSuite`;

  useEffect(() => {
    QRCode.toDataURL(upiUri, { margin: 1, width: 240, color: { dark: NAVY } })
      .then(setQrDataUrl).catch(() => setQrDataUrl(''));
  }, [upiUri]);

  const reasonLabel = {
    no_entitlement: 'No active access',
    tenure_expired: 'Your access period has ended',
    monthly_cap_exhausted: 'This month\'s discharge cap is exhausted',
    revoked: 'Your access was revoked by the platform',
  }[status.reason] || 'Access inactive';

  const redeem = async () => {
    const c = code.trim().toUpperCase();
    if (c.length < 4) { setErr('Enter the code you received'); return; }
    setBusy(true); setErr('');
    try {
      const { data } = await api.post('/surgical/entitlement/redeem', { code: c });
      toast.success(`Access activated for ${data.days_left} days · ${data.max_discharges_per_period} discharges/month`);
      onActivated?.(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not activate this code');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: '#fdfcf7' }} data-testid="surg-gate">
      <div className="w-full max-w-4xl bg-white rounded-3xl border-2 shadow-xl overflow-hidden" style={{ borderColor: NAVY }}>
        <div className="px-6 py-4 text-center" style={{ background: NAVY, color: '#fff' }}>
          <div className="text-[10px] font-bold uppercase tracking-[.24em] opacity-70">DocSite Surgical Suite</div>
          <div className="text-lg font-black mt-1">IPD Discharge Summary — Access Gate</div>
          <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full bg-rose-500/20 border border-rose-300/40 text-rose-100 text-[11px] font-semibold" data-testid="surg-gate-reason">
            <AlertCircle size={12} /> {reasonLabel}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-0">
          {/* ── Left: UPI payment ── */}
          <div className="p-6 border-r border-slate-200">
            <div className="text-[10px] font-bold uppercase tracking-[.14em]" style={{ color: TEAL }}>Step 1 — Pay Offline</div>
            <div className="mt-1" style={{ fontFamily: 'Georgia, serif', fontSize: 22, fontWeight: 800, color: NAVY }}>₹{price.toLocaleString('en-IN')} <span className="text-sm font-normal text-slate-500">/ month</span></div>
            <div className="text-xs text-slate-600 mt-0.5">Up to <b>1,000 discharges/month</b> · no carry-over</div>

            <div className="mt-4 flex items-start gap-4">
              <div className="p-2 bg-white rounded-xl border-2" style={{ borderColor: NAVY }}>
                {qrDataUrl ? <img src={qrDataUrl} alt="UPI QR" className="w-40 h-40" data-testid="surg-gate-qr" /> : <div className="w-40 h-40 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">UPI VPA</div>
                <div className="flex items-center gap-2">
                  <code className="text-[13px] font-mono font-bold px-2 py-1 rounded bg-slate-100" style={{ color: NAVY }} data-testid="surg-gate-vpa">{vpa}</code>
                  <button type="button" onClick={() => { navigator.clipboard?.writeText(vpa); toast.success('UPI ID copied'); }}
                          className="text-[11px] font-semibold text-teal-700 hover:underline" data-testid="surg-gate-copy-vpa">Copy</button>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-3 mb-1">Payee</div>
                <div className="text-[12.5px] font-semibold" style={{ color: NAVY }}>{payee}</div>
              </div>
            </div>

            <div className="mt-4 space-y-1.5 text-[11.5px] text-slate-700">
              <div className="flex gap-2"><span className="font-bold" style={{ color: TEAL }}>①</span><span>Open any UPI app (GPay, PhonePe, Paytm) and scan the QR — or send to <span className="font-mono">{vpa}</span>.</span></div>
              <div className="flex gap-2"><span className="font-bold" style={{ color: TEAL }}>②</span><span>Send the payment success screenshot to the platform via GPay chat / WhatsApp.</span></div>
              <div className="flex gap-2"><span className="font-bold" style={{ color: TEAL }}>③</span><span>You will receive a personal access code within minutes — enter it on the right to unlock.</span></div>
            </div>
          </div>

          {/* ── Right: Coupon redeem ── */}
          <div className="p-6" style={{ background: '#fdfcf7' }}>
            <div className="text-[10px] font-bold uppercase tracking-[.14em]" style={{ color: TEAL }}>Step 2 — Redeem your access code</div>
            <div className="mt-1 text-[16px] font-bold" style={{ color: NAVY }}>Enter the code you received</div>

            <div className="mt-4">
              <input type="text" value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setErr(''); }}
                     placeholder="E.G. RAINA-30D-100"
                     className="w-full px-4 py-3 rounded-xl border-2 border-slate-300 text-[15px] font-mono tracking-widest outline-none focus:border-teal-500"
                     style={{ color: NAVY, background: '#fff' }}
                     data-testid="surg-gate-code-input" />
              {err && <div className="mt-2 text-[12px] text-rose-600 font-semibold" data-testid="surg-gate-error">{err}</div>}

              <button type="button" onClick={redeem} disabled={busy}
                      className="mt-3 w-full py-3 rounded-xl font-bold text-white text-[14px] flex items-center justify-center gap-2 disabled:opacity-60"
                      style={{ background: TEAL }} data-testid="surg-gate-redeem">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                Activate Surgical Suite
              </button>
            </div>

            <div className="mt-5 p-3 rounded-xl border border-slate-200 bg-white">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5"><Timer size={11} /> What you get</div>
              <ul className="text-[11.5px] text-slate-700 space-y-1">
                <li>• Full IPD Discharge Composer with voice dictation & A4 PDF</li>
                <li>• Up to 1,000 sealed discharges per month (period resets on the 1st)</li>
                <li>• Access auto-locks when tenure OR monthly cap ends — whichever comes first</li>
                <li>• Coupon codes are per-doctor and cannot be shared</li>
              </ul>
            </div>

            {status.reason === 'tenure_expired' && (
              <div className="mt-3 text-[11px] text-slate-500 flex items-start gap-1.5"><Ticket size={11} className="mt-0.5" /><span>Your last code <span className="font-mono">{status.last_coupon_code}</span> expired on {(status.expires_at || '').slice(0, 10)}.</span></div>
            )}
          </div>
        </div>

        <div className="px-6 py-3 text-center text-[10px] text-slate-500" style={{ borderTop: '1px solid #e5e7eb' }}>
          Built with <span style={{ color: '#dc2626' }}>♥</span> in India · DocSite Clinical Management Platform · docsite.care
        </div>
      </div>
    </div>
  );
}
